import React from "react";
import { ArrowUpRight, Instagram, Facebook, Globe, Music2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useReveal, revealClass } from "../hooks/useReveal";

const IMG = {
  proxima:
    "https://images.unsplash.com/photo-1629217855633-79a6925d6c47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxhbWF0ZXVyJTIwZm9vdGJhbGwlMjBtYXRjaCUyMG5pZ2h0JTIwc3RhZGl1bXxlbnwwfHx8fDE3ODEyOTQ2MDN8MA&ixlib=rb-4.1.0&q=85",
  solibrum:
    "https://images.unsplash.com/photo-1592833159057-6faf163494a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMGxhbmRzY2FwZSUyMG1vZGVybnxlbnwwfHx8fDE3ODEyOTQ2MDN8MA&ixlib=rb-4.1.0&q=85",
  athletic:
    "https://images.unsplash.com/photo-1598881034666-6d3443d4b1bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxmb290YmFsbCUyMHRlYW0lMjBjb21tdW5pdHklMjB0cmFpbmluZ3xlbnwwfHx8fDE3ODEyOTQ2MDN8MA&ixlib=rb-4.1.0&q=85",
};

export const Projects = () => {
  const { t } = useLanguage();
  const [headRef, headVisible] = useReveal();

  const items = [
    {
      id: "proxima",
      number: "I",
      image: IMG.proxima,
      data: t.projects.items.proxima,
      links: [
        { type: "web", label: "proximaleague.com", href: "https://proximaleague.com", icon: Globe },
        { type: "ig", label: "@proximaleague", href: "https://instagram.com/proximaleague", icon: Instagram },
        { type: "fb", label: "@proximaleague", href: "https://facebook.com/proximaleague", icon: Facebook },
      ],
      primary: "https://proximaleague.com",
    },
    {
      id: "solibrum",
      number: "II",
      image: IMG.solibrum,
      data: t.projects.items.solibrum,
      links: [
        { type: "ig", label: "@solibrum", href: "https://instagram.com/solibrum", icon: Instagram },
      ],
      primary: "https://instagram.com/solibrum",
    },
    {
      id: "athletic",
      number: "III",
      image: IMG.athletic,
      data: t.projects.items.athletic,
      links: [
        { type: "ig", label: "@athleticgironi", href: "https://instagram.com/athleticgironi", icon: Instagram },
        { type: "tk", label: "@athleticgironi", href: "https://tiktok.com/@athleticgironi", icon: Music2 },
      ],
      primary: "https://instagram.com/athleticgironi",
    },
  ];

  return (
    <section
      id="projects"
      data-testid="section-projects"
      className="py-24 md:py-32 border-t border-line bg-surface scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={headRef} className={`${revealClass(headVisible)} mb-16 md:mb-20 grid grid-cols-12 gap-y-6 md:gap-x-16`}>
          <div className="col-span-12 md:col-span-4">
            <p className="section-index mb-4" data-testid="projects-index">{t.projects.index}</p>
            <h2
              className="font-serif-display font-light tracking-tight text-ink text-5xl md:text-6xl leading-none"
              data-testid="projects-title"
            >
              {t.projects.title}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6 flex items-end">
            <p className="text-lg text-ink-soft font-light max-w-xl" data-testid="projects-kicker">
              {t.projects.kicker}
            </p>
          </div>
        </div>

        <div className="space-y-20 md:space-y-28">
          {items.map((item, idx) => (
            <ProjectRow key={item.id} item={item} reverse={idx % 2 === 1} viewLabel={t.projects.view} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ProjectRow = ({ item, reverse, viewLabel }) => {
  const [rowRef, rowVisible] = useReveal();
  return (
    <article
      ref={rowRef}
      className={`${revealClass(rowVisible)} grid grid-cols-12 gap-y-8 md:gap-x-12 items-center`}
      data-testid={`project-${item.id}`}
    >
      <div
        className={`col-span-12 md:col-span-7 ${
          reverse ? "md:order-2" : ""
        }`}
      >
        <a
          href={item.primary}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`project-${item.id}-image-link`}
          className="block editorial-img bg-line"
        >
          <div className="aspect-[4/3] w-full overflow-hidden">
            <img
              src={item.image}
              alt={item.data.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </a>
      </div>

      <div
        className={`col-span-12 md:col-span-5 ${
          reverse ? "md:order-1 md:pr-8" : "md:pl-8"
        }`}
      >
        <div className="flex items-center gap-4 mb-5">
          <span className="font-serif-display text-3xl text-navy leading-none">
            {item.number}
          </span>
          <span className="h-px w-12 bg-line" />
          <span className="eyebrow">{item.data.tag}</span>
        </div>
        <h3
          className="font-serif-display text-4xl md:text-5xl font-light text-ink leading-[1.02] tracking-tight"
          data-testid={`project-${item.id}-name`}
        >
          {item.data.name}
        </h3>
        <p
          className="mt-5 text-base md:text-lg text-ink-soft font-light leading-relaxed"
          data-testid={`project-${item.id}-summary`}
        >
          {item.data.summary}
        </p>

        <div className="mt-8 space-y-3">
          {item.links.map((l) => {
            const Icon = l.icon;
            return (
              <a
                key={`${item.id}-${l.type}`}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`project-${item.id}-link-${l.type}`}
                className="group flex items-center justify-between border-b border-line py-3 hover:border-ink transition-colors"
              >
                <span className="flex items-center gap-3 text-sm text-ink-soft group-hover:text-ink transition-colors">
                  <Icon size={15} strokeWidth={1.5} />
                  {l.label}
                </span>
                <ArrowUpRight
                  size={15}
                  className="text-ink-soft group-hover:text-ink group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                />
              </a>
            );
          })}
        </div>

        <a
          href={item.primary}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`project-${item.id}-cta`}
          className="mt-10 inline-flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase font-medium text-ink border-b border-ink pb-2 hover:opacity-70 transition-opacity"
        >
          {viewLabel}
          <ArrowUpRight size={14} />
        </a>
      </div>
    </article>
  );
};

export default Projects;
